/** @format */

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  ArrowRightLeft,
  ExternalLink,
  GitCompare,
  Loader2,
  Menu,
  ScanSearch,
} from "lucide-react";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FLAG_COMPARE_VERSIONS,
  FLAG_RMA_ANALYSIS,
  useFeatureFlagEnabled,
} from "../../lib/featureFlags";
import { useSopStore } from "../../store/useSopStore";
import { ProcessingStatusEnum, SOP } from "../../types/sop";

interface SOPItemProps {
  sop: SOP;
  canEdit?: boolean;
  /** When true the user can access the RMA analysis feature (viewer+). */
  canAccessRMA?: boolean;
  canEditWhenProcessing?: boolean;
  onViewClick?: (sop: SOP) => void;
  isNavigating?: boolean;
  isSuperAdmin?: boolean;
  displayOrgName?: string;
  onMigrateClick?: () => void;
  /** When set, row uses explicit grid columns (no col-span). */
  gridTemplateColumns?: string;
}

const SOPItem: FC<SOPItemProps> = ({
  sop,
  canEdit: _canEdit = false,
  canAccessRMA = false,
  canEditWhenProcessing = false,
  onViewClick,
  isNavigating: isNavigatingProp = false,
  isSuperAdmin = false,
  displayOrgName,
  onMigrateClick,
  gridTemplateColumns,
}) => {
  // WORKAROUND: see lib/WORKAROUND_feature_flags.md
  const rmaEnabled = useFeatureFlagEnabled(FLAG_RMA_ANALYSIS);
  const compareVersionsEnabled = useFeatureFlagEnabled(FLAG_COMPARE_VERSIONS);

  const { fetchSOPById } = useSopStore();
  const [isNavigatingLocal, setIsNavigatingLocal] = useState(false);
  const isNavigating = isNavigatingProp || isNavigatingLocal;
  const displayTitle = sop.getDisplayTitle();

  const navigate = useNavigate();
  const isProcessingOrPending =
    sop.processingStatus === ProcessingStatusEnum.PROCESSING ||
    sop.processingStatus === ProcessingStatusEnum.QUEUED ||
    sop.processingStatus === ProcessingStatusEnum.PENDING_VERIFICATION;
  const showViewButton = canEditWhenProcessing || !isProcessingOrPending;

  const handleViewClick = async () => {
    if (onViewClick) {
      onViewClick(sop);
      return;
    }
    setIsNavigatingLocal(true);
    try {
      await fetchSOPById(sop.sopId);
      navigate(`/sop/${sop.sopId}`);
    } catch {
      setIsNavigatingLocal(false);
    }
  };

  const locale = navigator.language;
  const createdDate = new Date(sop.createdAt + "Z");
  const dateShort =
    createdDate.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    createdDate.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  const dateFull = createdDate.toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });

  const rowClass =
    "grid gap-4 p-4 hover:bg-gradient-to-r hover:from-slate-700/20 hover:to-slate-800/20 transition-all duration-200 group" +
    (gridTemplateColumns ? "" : " grid-cols-12");
  const titleCellClass = "flex items-center min-w-0";
  const creatorCellClass = gridTemplateColumns
    ? "flex items-center min-w-0"
    : "col-span-2 flex items-center";
  const orgCellClass = gridTemplateColumns
    ? "flex items-center min-w-0"
    : "col-span-2 flex items-center";
  const opsCellClass = gridTemplateColumns
    ? "flex items-center justify-center"
    : "col-span-1 flex items-center justify-center";
  const createdCellClass = gridTemplateColumns
    ? "flex items-center min-w-0"
    : "col-span-2 flex items-center min-w-0";
  const actionsCellClass = gridTemplateColumns
    ? "flex items-center justify-center gap-2 min-w-0"
    : "col-span-2 flex items-center justify-center gap-2 min-w-[140px] shrink-0";

  return (
    <div
      className={rowClass}
      style={gridTemplateColumns ? { gridTemplateColumns } : undefined}
    >
      {/* Title Column */}
      <div className={titleCellClass}>
        <div className="flex items-center gap-3 w-full">
          <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full shrink-0" />
          <p className="text-white font-medium truncate min-w-0">
            {displayTitle}
          </p>
        </div>
      </div>

      {/* Creator Column */}
      <div className={creatorCellClass}>
        <span
          className="text-slate-300 text-sm truncate block min-w-0"
          title={sop.creatorEmail ?? undefined}
        >
          {sop.creatorEmail ?? "—"}
        </span>
      </div>

      {/* Organization Column (SuperAdmin only) */}
      {isSuperAdmin && (
        <div className={orgCellClass}>
          <span
            className="text-slate-300 text-sm truncate block min-w-0"
            title={sop.organizationName ?? displayOrgName ?? undefined}
          >
            {sop.organizationName ?? displayOrgName ?? "No org"}
          </span>
        </div>
      )}

      {/* Operations Column */}
      <div className={opsCellClass}>
        <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg px-3 py-1">
          <span className="text-cyan-300 font-semibold">
            {sop.processingStatus === ProcessingStatusEnum.DONE ||
              sop.processingStatus === ProcessingStatusEnum.COMPLETED ||
              sop.processingStatus === ProcessingStatusEnum.REGENERATE
              ? sop.getOperationCount()
              : 0}
          </span>
        </div>
      </div>

      {/* Created At Column */}
      <div className={createdCellClass}>
        <span
          className="text-slate-300 text-sm whitespace-nowrap"
          title={dateFull}
        >
          {dateShort}
        </span>
      </div>

      {/* Actions Column */}
      <div className={actionsCellClass}>
        {showViewButton ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-2 text-slate-300 hover:bg-slate-600/40 hover:text-white transition-colors cursor-pointer"
                aria-label="SOP actions"
                onClick={(e) => e.stopPropagation()}
              >
                <Menu className="w-4 h-4" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[200px] rounded-lg border border-slate-600/50 bg-slate-800 py-1 shadow-xl z-50"
                align="end"
                sideOffset={4}
                onClick={(e) => e.stopPropagation()}
              >
                {compareVersionsEnabled !== false && (
                  <DropdownMenu.Item
                    className="flex cursor-pointer select-none items-center gap-2 rounded px-3 py-2 text-sm text-slate-200 outline-none hover:bg-slate-700/80 hover:text-white focus:bg-slate-700/80 data-[highlighted]:bg-slate-700/80"
                    onSelect={() =>
                      navigate(`/sop/${sop.sopId}/versions`, {
                        state: { from: "/sop-dashboard" },
                      })
                    }
                  >
                    <GitCompare className="w-4 h-4 shrink-0 text-slate-400" />
                    Compare versions
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  className="flex cursor-pointer select-none items-center gap-2 rounded px-3 py-2 text-sm text-slate-200 outline-none hover:bg-slate-700/80 hover:text-white focus:bg-slate-700/80 data-[highlighted]:bg-slate-700/80 disabled:opacity-50"
                  onSelect={() => handleViewClick()}
                  disabled={isNavigating}
                >
                  {isNavigating ? (
                    <Loader2 className="w-4 h-4 shrink-0 text-blue-400 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 shrink-0 text-slate-400" />
                  )}
                  Open SOP
                </DropdownMenu.Item>
                {canAccessRMA && rmaEnabled !== false && (
                  <DropdownMenu.Item
                    className="flex cursor-pointer select-none items-center gap-2 rounded px-3 py-2 text-sm text-slate-200 outline-none hover:bg-slate-700/80 hover:text-white focus:bg-slate-700/80 data-[highlighted]:bg-slate-700/80"
                    onSelect={() => navigate(`/sop/${sop.sopId}/rma`)}
                  >
                    <ScanSearch className="w-4 h-4 shrink-0 text-cyan-400" />
                    RMA Defect Analysis
                  </DropdownMenu.Item>
                )}
                {onMigrateClick && (
                  <DropdownMenu.Item
                    className="flex cursor-pointer select-none items-center gap-2 rounded px-3 py-2 text-sm text-slate-200 outline-none hover:bg-slate-700/80 hover:text-white focus:bg-slate-700/80 data-[highlighted]:bg-slate-700/80"
                    onSelect={() => onMigrateClick()}
                  >
                    <ArrowRightLeft className="w-4 h-4 shrink-0 text-amber-400" />
                    Change organization
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : (
          <div
            className="flex items-center justify-center rounded-lg p-2 bg-slate-700/30 border border-slate-600/30"
            title="Processing..."
          >
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SOPItem;
